import React, { useEffect, useState, Suspense } from "react";
import { useHistory } from "react-router-dom";
import usePaginationValuesWhere from '../../hooks/usePaginationValuesWhere';
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { GridView, SvgComponent } from 'components';
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { ItemManager } from "managers";
import { Item } from "models/ECF/Item";
import Select from "react-select";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
import ItemEdit from "./ItemEdit";
import Exportar from "components/Exportar/Exportar";

export default function ItemList({ hideLoading, showLoading, isActive }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [idSec, setIdSec] = useState(undefined);
  const [showCanvas, setShowCanvas] = useState(false);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const [showExportar, setShowExportar] = useState(false);
  const { cerrarAlertas } = useMessage();
  const history = useHistory();

  useEffect(() => {
    if (isActive) {
      buscar();
    }
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where]);

  const updateList = () => {
    buscar();
  };

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };
    ItemManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        const transformedList = res.Data.map(item => ({
          ...item,
          IndicadorFacturacion: Item.getFacturacionTexto(item.IndicadorFacturacion),
          IndicadorBienOServicio: Item.getIndicadorBienServicioTexto(item.IndicadorBienOServicio),
          Estatus: Item.getEstatusTexto(item.Estatus),
        }));
        setList(transformedList);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (params) => {
    if (params.IdSec) {
      setIdSec(params.IdSec);
      setShowCanvas(true);
    } else {
      setIdSec(undefined);
      setShowCanvas(true);
    }
  };

  const openExportar = () => {
    setShowExportar(true);
  };


  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader crearNuevo={handleClick} exportar={openExportar} />
      </div>

      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={Item.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            serialize={true}  
            exportarCallBack={ItemManager.exportar}
          ></Exportar>
        </Suspense>
      )}

      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>
          <ItemEdit
            showCanvas={showCanvas}
            setShowCanvas={setShowCanvas}
            IdSec={idSec}
            hideLoading={hideLoading}
            showLoading={showLoading}
            updateList={updateList}
          />
        </Suspense>
      )}

      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
      >
        <GridView.Column
          isField
          width="100px"
          showClearFilter={true}
          bodyRenderer={(row) => {
            return (
              <div className="float-right">
                <IconButton onClick={() => handleClick({ IdSec: row.IdSec })}>
                  <ToolTip message="Editar">
                    <div>
                      <SvgComponent
                        iconName="pencil"
                        height="25"
                        width="25"
                      />
                    </div>
                  </ToolTip>
                </IconButton>
              </div>
            );
          }}
        />
        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
          width="110px"
        />
        <GridView.Column
          headerText="Nombre"
          dataField="Nombre"
          sortExpression="Nombre"
          width="250px"
        />
        <GridView.Column
          headerText="Código de Barra"
          dataField="CodigoBarra"
          sortExpression="CodigoBarra"
          width="250px"
        />
        <GridView.Column
          headerText="Descripción"
          dataField="Descripcion"
          sortExpression="Descripcion"
          width="300px"
        />
        <GridView.Column
          headerText="Tipo ITBIS"
          dataField="IndicadorFacturacion"
          sortExpression="IndicadorFacturacion"
          width="200px"
          filterRenderer={() => {
            const selectedValue = Item.INDICADOR_FACTURACION_LIST.find(
              (option) => option.value === where.IndicadorFacturacion
            );

            return (
              <Select
                className="react-select primary"
                options={Item.INDICADOR_FACTURACION_LIST}
                value={selectedValue}
                name="IndicadorFacturacion"
                onChange={(selectedOption) => {
                  setWhere({
                    ...where,
                    IndicadorFacturacion: selectedOption ? selectedOption.value : null,
                  });
                }}
              />
            );
          }}
        />
        <GridView.Column
          headerText="Tipo"
          dataField="IndicadorBienOServicio"
          sortExpression="IndicadorBienOServicio"
          width="210px"
          filterRenderer={() => {
            const selectedValue = Item.INDICADOR_BIEN_O_SERVICIO_LIST.find(
              (option) => option.value === where.IndicadorBienOServicio
            );

            return (
              <Select
                className="react-select primary"
                options={Item.INDICADOR_BIEN_O_SERVICIO_LIST}
                value={selectedValue}
                name="IndicadorBienOServicio"
                onChange={(selectedOption) => {
                  setWhere({
                    ...where,
                    IndicadorBienOServicio: selectedOption ? selectedOption.value : null,
                  });
                }}
              />
            );
          }}
        />
        <GridView.Column
          headerText="Precio"
          dataField="PrecioUnitario"
          sortExpression="PrecioUnitario"
          width="150px"
          number={true}
        />
        <GridView.Column
          headerText="Estatus"
          dataField="Estatus"
          sortExpression="Estatus"
          width="175px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={Item.ESTATUS_LIST}
                value={Item.ESTATUS_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.Estatus ? where.Estatus : Item.ESTATUS_LIST[0].value)
                  );
                })}
                name="Estatus"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    Estatus: e.value,
                  })
                }
              />
            );
          }}
        />
      </GridView>
    </>
  );
}